/**
 * PostRoundPane — shown between rounds. Three layouts based on settings:
 *   - vote: voteOptions tiles, countdown bar, live tally bubbles
 *   - host: tile catalog (filtered by allowedGames), host taps to pick
 *   - random: card-flip reveal (non-interactive)
 */
import { useEffect, useState } from 'react';
import { MAX_PLAYERS } from '@tt/players';
import type { LobbyView, RoundResult, RoundXp } from '@tt/lobby';
import { getCharacterStats, type CharacterStats, type GameTile } from '../../lib/api';
import { Character3DThumbnail } from '../characters/Character3DThumbnail';
import { RoundXpSummary } from '../me/RoundXpSummary';
import { GameThumb } from './GameThumb';
import { VoteTiles } from './VoteTiles';
import { WinnerPodium3D, type PodiumEntry } from './WinnerPodium3D';
import { gameAvailability, livePlayerCount, playerReqLabel } from './gameAvailability';

interface Props {
  lobby: LobbyView;
  result: RoundResult | null;
  vote: { gameIds: string[]; endsAt: number; tally: Record<string, number> } | null;
  games: GameTile[];
  myCharId: string;
  isHost: boolean;
  onVote: (gameId: string) => void;
  onPickHost: (gameId: string) => void;
  onSkip: () => void;
  onInspectPlayer?: (charId: string) => void;
}

export function PostRoundPane({ lobby, result, vote, games, myCharId, isHost, onVote, onPickHost, onSkip, onInspectPlayer }: Props) {
  const settings = lobby.settings;
  const ranking = [...lobby.players].sort((a, b) => b.lobbyScore - a.lobbyScore);
  const recap = buildRecap(lobby, result, games);
  // WS-4 — current standings leaders as cosmetic-matched 3D characters on a
  // podium (1st centre/tallest, 2nd/3rd flanking). Presentation over the same
  // ranking the standings strip below uses.
  const podiumEntries: PodiumEntry[] = ranking
    .filter((p) => !p.isSpectator)
    .slice(0, 3)
    .map((p) => ({ charId: p.charId, name: p.name, icon: p.icon, points: p.lobbyScore }));
  const myXp = result?.xp?.[myCharId];
  const [statsByChar, setStatsByChar] = useState<Record<string, CharacterStats>>({});

  // R1#25: previously fetched getCharacterStats for EVERY player every
  // round end — N Lambda invocations per roundEnd transition for
  // background flair (favorite-game / round-wins captions on the player
  // strip). That's an unnecessary fan-out and a per-round cost that
  // grows with table size. Now we fetch ONLY the local user's stats; the
  // other-player chips fall back to lobbyScore (already shipped on the
  // LobbyView) for their caption. If you ever need richer per-player
  // flair here, consider piggybacking on the round result payload or
  // adding a batch endpoint instead of N solo fetches.
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(myCharId);
        if (!live) return;
        setStatsByChar({ [myCharId]: s });
      } catch { /* */ }
    })();
    return () => { live = false; };
  }, [myCharId]);

  const heroTitle =
    settings.pickWho === 'vote' ? 'Pick the next game'
    : settings.pickWho === 'host' ? 'Choosing the next game'
    : 'Drawing the next game';

  return (
    <section className="post-round">
      {/* Parchment scroll round-wrap header — per docs/ui/voting-screens.md §3.2 (P1). */}
      <header className="round-wrap-scroll">
        <span className="round-wrap-seal" aria-hidden="true">T</span>
        <div className="round-wrap-line">
          Round {lobby.setRoundsPlayed} of {settings.mode === 'free' ? '∞' : settings.roundsPerSet}
        </div>
        <div className="round-wrap-game">
          {result ? (findGameTile(games, result.gameId)?.name ?? result.gameId) : 'Stand by'} wraps
        </div>
      </header>

      {/* Next-game pick hero — the primary action for players. */}
      <div className="next-game-hero">
        <h3 className="next-game-hero-title">{heroTitle}</h3>
        {settings.pickWho === 'vote' && vote && (
          <VoteTiles
            vote={vote}
            games={games}
            myCharId={myCharId}
            lobby={lobby}
            statsByChar={statsByChar}
            onVote={onVote}
            onInspectPlayer={onInspectPlayer}
            showPlayerStrip={false}
          />
        )}
        {settings.pickWho === 'host' && (
          <HostPickSection
            games={games.filter((g) => !settings.allowedGames.length || settings.allowedGames.includes(g.id) || (g.baseGameId ? settings.allowedGames.includes(g.baseGameId) : false))}
            playerCount={livePlayerCount(lobby)}
            isHost={isHost}
            onPick={onPickHost}
          />
        )}
        {settings.pickWho === 'random' && (
          <RandomRevealSection isHost={isHost} onSkip={onSkip} />
        )}
      </div>

      {podiumEntries.length > 0 && (
        <WinnerPodium3D
          entries={podiumEntries}
          className="winner-podium-3d"
          style={{ width: '100%', maxWidth: 520, margin: '0 auto', height: 'clamp(200px, 32vh, 300px)' }}
        />
      )}

      {/* Standings strip — compact 3D-portrait rows replacing the tankard rack. */}
      <div className="standings-strip" aria-label="standings">
        {ranking.map((p, i) => {
          const delta = result?.lobbyDelta?.[p.charId] ?? 0;
          const globalDelta = result?.globalDelta?.[p.charId] ?? 0;
          const isMe = p.charId === myCharId;
          return (
            <button
              key={p.charId}
              type="button"
              className={`standings-entry${isMe ? ' is-me' : ''}`}
              onClick={() => onInspectPlayer?.(p.charId)}
              aria-label={`rank ${i + 1}: ${p.name}, ${p.lobbyScore} points`}
            >
              <span className={`standings-medal rank-${i + 1}`}>
                <span className="standings-medal-num">{i + 1}</span>
                <span className="standings-medal-ord">{ordinalSuffix(i + 1)}</span>
              </span>
              <Character3DThumbnail icon={p.icon} frame="portrait" size={44} />
              <span className="standings-name">{p.name}</span>
              <span className="standings-score">{p.lobbyScore} pts</span>
              {delta > 0 && <span className="standings-delta">+{delta}</span>}
              {globalDelta > 0 && <span className="standings-global">global +{globalDelta}</span>}
            </button>
          );
        })}
      </div>
      {myXp && myXp.total > 0 && <XpItemize xp={myXp} />}

      {/* Chalkboard recap — per §3.3 (P2/P3). */}
      <div className="round-recap-board" aria-label="round recap">
        <span className="round-recap-rope round-recap-rope-l" aria-hidden="true" />
        <span className="round-recap-rope round-recap-rope-r" aria-hidden="true" />
        <div className="round-recap-slate">
          {recap.map((item) => (
            <div key={item.label} className="round-recap-entry">
              <span className="round-recap-label">{item.label}</span>
              <strong className="round-recap-value">{item.value}</strong>
              <small className="round-recap-detail">{item.detail}</small>
            </div>
          ))}
        </div>
      </div>

      {result?.awards && result.awards.length > 0 && (
        <div className="awards-row">
          {result.awards.map((a, i) => {
            const scoring = isScoringAward(a, result);
            const name = lobby.players.find((p) => p.charId === a.charId)?.name;
            return (
              <span key={i} className={`award-chip${scoring ? ' scoring' : ' style-only'}`} title={scoring ? 'This award added set points.' : 'Style award. No extra score.'}>
                {name ? `${name}: ` : ''}{a.label}<small>{scoring ? 'scoring' : 'style'}</small>
              </span>
            );
          })}
        </div>
      )}

      {myXp && myXp.total > 0 && <RoundXpSummary xp={myXp} />}
    </section>
  );
}

function ordinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function findGameTile(games: GameTile[], gameId: string): GameTile | undefined {
  return games.find((g) => g.id === gameId) ?? games.find((g) => g.baseGameId === gameId);
}

function isScoringAward(award: { charId: string; label: string }, result: RoundResult): boolean {
  const labelsWithPoints = /bonus|\+|comeback|clutch|mvp/i;
  return labelsWithPoints.test(award.label) && (result.lobbyDelta[award.charId] ?? 0) > 0;
}

function spectatorDetail(lobby: LobbyView): string {
  const watching = lobby.players.filter((p) => p.isSpectator).length;
  return watching > 0 ? `Players seated · ${watching} watching.` : 'Players seated.';
}

function buildRecap(lobby: LobbyView, result: RoundResult | null, games: GameTile[]): Array<{ label: string; value: string; detail: string }> {
  if (!result) {
    return [
      { label: 'Status', value: 'Waiting', detail: 'Round data is syncing.' },
      // Seated players vs capacity; spectators shown separately (a watcher
      // must never read as an over-full table — the "5/4" bug).
      { label: 'Table', value: `${lobby.players.filter((p) => !p.isSpectator).length}/${MAX_PLAYERS}`, detail: spectatorDetail(lobby) },
      { label: 'Event', value: eventModeLabel(lobby.settings.eventMode), detail: 'Current night mode.' },
    ];
  }

  const game = findGameTile(games, result.gameId);
  const roundWinner = lobby.players.find((p) => p.charId === result.ranks[0]);
  const biggestDelta = Object.entries(result.lobbyDelta).sort((a, b) => b[1] - a[1])[0];
  const mover = biggestDelta ? lobby.players.find((p) => p.charId === biggestDelta[0]) : null;
  const bestScore = Math.max(0, ...Object.values(result.scores));
  const award = result.awards[0];
  const awardPlayer = award ? lobby.players.find((p) => p.charId === award.charId) : null;

  return [
    { label: 'Winner', value: roundWinner?.name ?? 'No winner', detail: game?.name ?? result.gameId },
    { label: 'Best score', value: String(bestScore), detail: mover && biggestDelta?.[1] ? `${mover.name} gained +${biggestDelta[1]}` : 'No table points awarded.' },
    { label: 'Highlight', value: awardPlayer?.name ?? eventModeLabel(lobby.settings.eventMode), detail: award?.label ?? 'Clean round, no bonus awards.' },
  ];
}

function eventModeLabel(mode: LobbyView['settings']['eventMode']): string {
  switch (mode) {
    case 'lightning': return 'Lightning Night';
    case 'comeback': return 'Comeback Night';
    case 'chaos': return 'Chaos Night';
    case 'cozy': return 'Cozy Night';
    case 'standard':
    default: return 'Standard Night';
  }
}

function HostPickSection({ games, playerCount, isHost, onPick }: { games: GameTile[]; playerCount: number; isHost: boolean; onPick: (id: string) => void }) {
  if (!isHost) return <p className="muted">The host is choosing the next game…</p>;
  return (
    <div className="host-pick">
      <div className="vote-grid">
        {games.map((g) => {
          const availability = gameAvailability(g, playerCount);
          return (
            <button
              key={g.id}
              type="button"
              className={`vote-tile${availability.playable ? '' : ' disabled'}`}
              onClick={() => { if (availability.playable) onPick(g.id); }}
              aria-label={availability.playable ? `pick ${g.name}` : `${g.name} unavailable: ${availability.reason}`}
              disabled={!availability.playable}
            >
              <GameThumb gameId={g.id} category={g.category} name={g.name} chaoticness={g.chaoticness} />
              <div className="tile-name">{g.name}</div>
              <div className="tile-tag muted">{availability.reason ?? g.pitch}</div>
              <div className="tile-meta muted">{playerReqLabel(g)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function XpItemize({ xp }: { xp: RoundXp }) {
  return (
    <div className="xp-itemize" aria-label="XP earned this round">
      {xp.items.map((it, i) => (
        <div key={i} className="xp-itemize-row">
          <span className="xp-itemize-label">{it.label}</span>
          <span className="xp-itemize-amount">+{it.amount}</span>
        </div>
      ))}
      <div className="xp-itemize-total">
        <span>+{xp.total} XP</span>
        <span>Lv {xp.levelAfter} · {xp.toNext.current}/{xp.toNext.needed}</span>
      </div>
      {xp.levelAfter > xp.levelBefore && (
        <div className="xp-itemize-levelup">
          Level up! {xp.levelBefore} → {xp.levelAfter}
        </div>
      )}
    </div>
  );
}

function RandomRevealSection({ isHost, onSkip }: { isHost: boolean; onSkip: () => void }) {
  return (
    <div className="random-reveal">
      <div className="reveal-card">drawing the next card…</div>
      {isHost && (
        <button type="button" className="btn ghost" onClick={onSkip}>Skip</button>
      )}
    </div>
  );
}
